import React from 'react';
import PropTypes from 'prop-types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

const COLORS = ['#00C49F', '#0088FE', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

const ObjectivesByRegulationChart = ({ data }) => {
  // Group objectives by regulatory reference
  const regulationCounts = {};
  data.forEach(obj => {
    const ref = obj.regulatory_reference || 'Unknown';
    regulationCounts[ref] = (regulationCounts[ref] || 0) + 1;
  });

  // Sort by count desc and take top 8 for readability
  const sortedEntries = Object.entries(regulationCounts)
    .map(([key, value]) => ({ regulation: key, count: value }))
    .sort((a, b) => b.count - a.count);

  const TOP_N = 8;
  const topItems = sortedEntries.slice(0, TOP_N);
  const remaining = sortedEntries.slice(TOP_N);
  const othersTotal = remaining.reduce((sum, item) => sum + item.count, 0);

  const chartData = [
    ...topItems.map((item, index) => ({
      regulation:
        item.regulation.length > 40
          ? item.regulation.substring(0, 40) + '...'
          : item.regulation,
      fullRegulation: item.regulation,
      count: item.count,
      color: COLORS[index % COLORS.length],
    })),
    ...(othersTotal > 0
      ? [{
          regulation: 'Other References',
          fullRegulation: 'Other References',
          count: othersTotal,
          color: '#9ca3af',
        }]
      : []),
  ];

  const hasData = chartData.length > 0;

  const renderCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          maxWidth: '300px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold', color: '#000', marginBottom: '5px' }}>
            {data.payload.fullRegulation || data.payload.regulation}
          </p>
          <p style={{ margin: 0, color: '#666' }}>
            {`${data.value} objective${data.value !== 1 ? 's' : ''}`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer height={320} width="100%">
      {hasData ? (
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis
            type="category"
            dataKey="regulation"
            width={180}
          />
          <Tooltip content={renderCustomTooltip} />
          <Bar dataKey="count" name="Number of Objectives" radius={[0, 8, 8, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
            <LabelList
              dataKey="count"
              position="right"
              formatter={(value) => value}
            />
          </Bar>
        </BarChart>
      ) : (
        <div style={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          color: '#888'
        }}>
          No Data Available
        </div>
      )}
    </ResponsiveContainer>
  );
};

ObjectivesByRegulationChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    regulatory_reference: PropTypes.string,
  })).isRequired,
};

export default ObjectivesByRegulationChart;

